import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Public CSAT page reached from the "Resolved" email link: /rate?t=<token>[&score=N]
 * Needs NO login — the signed token authorises exactly one ticket's rating.
 * If the email's star link carried &score=N we auto-submit that score, then
 * let the person optionally add a comment or change it.
 */
export default function Rate() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("t") || "";
  const presetScore = parseInt(params.get("score") || "", 10);

  const [state, setState] = useState({ loading: true, error: "", ticket: null, done: false });
  const [score, setScore] = useState(Number.isInteger(presetScore) && presetScore >= 1 && presetScore <= 5 ? presetScore : 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = useCallback(async (chosen) => {
    const s = chosen || score;
    if (!s) return;
    setSaving(true);
    try {
      await api.rateSubmit(token, s, comment);
      setState((p) => ({ ...p, done: true }));
    } catch (e) {
      setState((p) => ({ ...p, error: e.message || "Could not save your rating." }));
    } finally {
      setSaving(false);
    }
  }, [score, comment, token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setState({ loading: false, error: "This rating link is missing its code.", ticket: null, done: false }); return; }
      try {
        const r = await api.rateInfo(token);
        if (!alive) return;
        setState({ loading: false, error: "", ticket: r.ticket, done: !!r.existing, ticketExisting: r.existing });
        if (r.existing) { setScore(r.existing.score); setComment(r.existing.comment || ""); }
      } catch (e) {
        if (alive) setState({ loading: false, error: e.message || "This rating link is invalid or has expired.", ticket: null, done: false });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Auto-submit a preset star from the email, once, after we've validated the link.
  useEffect(() => {
    if (!state.loading && state.ticket && !state.done && score && Number.isInteger(presetScore)) {
      submit(presetScore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading]);

  const wrap = { minHeight: "100vh", display: "grid", placeItems: "center", background: "#f6f8fc", padding: 24, fontFamily: "Inter, system-ui, sans-serif" };
  const card = { width: "100%", maxWidth: 460, background: "#fff", borderRadius: 16, padding: 32, boxShadow: "0 10px 40px rgba(13,26,58,.08)", border: "1px solid #eef1f7" };

  if (state.loading) return <div style={wrap}><div style={card}>Loading…</div></div>;
  if (state.error) return <div style={wrap}><div style={card}><h2 style={{ margin: 0, color: "#0D1A3A" }}>Rating unavailable</h2><p style={{ color: "#5b6b8c" }}>{state.error}</p></div></div>;

  if (state.done) return (
    <div style={wrap}><div style={{ ...card, textAlign: "center" }}>
      <div style={{ fontSize: 40 }}>✓</div>
      <h2 style={{ margin: "8px 0", color: "#0D1A3A" }}>Thank you!</h2>
      <p style={{ color: "#5b6b8c" }}>Your feedback on <b>{state.ticket?.ticket_no}</b> has been recorded.</p>
    </div></div>
  );

  return (
    <div style={wrap}><div style={card}>
      <p style={{ color: "#0654FC", fontWeight: 600, margin: 0, fontSize: 13 }}>{state.ticket?.ticket_no}</p>
      <h2 style={{ margin: "4px 0 2px", color: "#0D1A3A" }}>How did we do?</h2>
      <p style={{ color: "#5b6b8c", marginTop: 0 }}>{state.ticket?.title}</p>

      <div style={{ display: "flex", gap: 6, justifyContent: "center", margin: "18px 0" }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setScore(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            aria-label={`${n} star`} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 38, lineHeight: 1,
              color: (hover || score) >= n ? "#1BA85C" : "#d6dce8", transition: "color .12s" }}>★</button>
        ))}
      </div>

      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
        placeholder="Anything you'd like to add? (optional)"
        style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, border: "1px solid #dce1ec", padding: 12, resize: "vertical", fontFamily: "inherit" }} />

      <button disabled={!score || saving} onClick={() => submit()}
        style={{ width: "100%", marginTop: 14, padding: "12px 16px", borderRadius: 10, border: "none", cursor: score ? "pointer" : "not-allowed",
          background: score ? "#0654FC" : "#b9c4dc", color: "#fff", fontWeight: 600, fontSize: 15 }}>
        {saving ? "Saving…" : "Submit rating"}
      </button>
    </div></div>
  );
}
